import * as React from 'react'
import { ReactElement } from 'react'
import { FieldArray, FormikContextType, useFormikContext } from 'formik'
import { FileMutationDto } from '../../../../config/fileConfig'
import { Stack, Toggle } from '@fluentui/react'
import UserTierList from '../../UserTier/UserTierList'
import { TierDto } from '../../../../config/finance/financeConfig'
import { UserTierListItem } from '../../UserTier/UserTierListItem'
import { UserTierFormProps } from '../../UserTier/UserTierForm'
import UserTierFormContainer from '../../UserTier/UserTierFormContainer'
import { UserDto } from '../../../../config/userConfig'
import { useSettings } from '../../../../hooks/useSettings'
import { useTranslation } from 'react-i18next'
import { CommitEventType } from '../../../Input/FormattableNumberInput'
import MoneyInput from '../../../Input/MoneyInput'
import { Field } from '@fluentui/react-components'
import { RateFieldProps } from './RateFields'
import BillableCombobox from '../../../Combobox/BillableCombobox'

export function getDefaultTierValue(currency: string): TierDto {
    return {
        type: 'TIER',
        purchaseRate: undefined,
        salesRate: '0.00',
        userId: undefined,
        currency: currency,
    }
}

export default function RateFieldsContents({ orientation }: RateFieldProps): ReactElement {
    const { t: translate } = useTranslation()

    const { initialValues, values, setFieldValue } = useFormikContext<FileMutationDto>()
    const settings = useSettings()

    return (
        <>
            <Field label={translate('file.edit.form.finance.rates.type.label')} required orientation={'horizontal'}>
                <Toggle
                    checked={!values.rate}
                    onText={translate('file.edit.form.finance.rates.type.tiers')}
                    offText={translate('file.edit.form.finance.rates.type.file')}
                    inlineLabel
                    onChange={(_: React.MouseEvent, checked?: boolean) => {
                        // Only update these values if the toggle for tiers/rate has been switched
                        setFieldValue(
                            'rate',
                            !checked ? (initialValues.rate ?? settings?.fileScaffold?.fileRate?.hourRate) : undefined,
                        )
                        setFieldValue('tiers', checked ? initialValues.tiers || [] : undefined)
                        setFieldValue('billable', checked ? settings?.fileScaffold?.billable : initialValues.billable)
                    }}
                    styles={{ root: { marginBottom: 0 } }}
                />
            </Field>
            <Field label={translate('base.billable.label')} required orientation={orientation}>
                <BillableCombobox
                    required
                    selectedOptions={[values.billable]}
                    onOptionSelect={(_, data) => setFieldValue('billable', data.selectedOptions?.[0])}
                />
            </Field>
            {!values.rate ? (
                <Stack>
                    <FieldArray name="tiers">
                        {() => (
                            <UserTierList<TierDto>
                                defaultTierValue={() => getDefaultTierValue(values.currency)}
                                createListItem={(index: number, item: TierDto, editTier) => (
                                    <UserTierListItem index={index} item={item} editTier={editTier} />
                                )}
                                createTierFormContainer={(
                                    props: UserTierFormProps<TierDto>,
                                    fileContext: FormikContextType<FileMutationDto>,
                                    selectableUsers: UserDto[],
                                ) => (
                                    <UserTierFormContainer
                                        {...props}
                                        fileContext={fileContext}
                                        selectableUsers={selectableUsers}
                                    />
                                )}
                            />
                        )}
                    </FieldArray>
                </Stack>
            ) : (
                <Field label={translate('file.edit.form.finance.rate')} required orientation={orientation}>
                    <MoneyInput
                        value={values?.rate}
                        currency={values.currency}
                        onCommit={(eventType: CommitEventType, value: string) => {
                            if (eventType !== CommitEventType.OnLoad) {
                                setFieldValue('rate', value)
                            }
                        }}
                    />
                </Field>
            )}
        </>
    )
}
